import { useEffect, useRef, useState } from "react";

// En glömd flik ska inte fråga servern i all oändlighet. Fliken somnar
//  - när den legat dold i 30 minuter (hämtningarna pausar redan när den är
//    dold; det som stängs då är realtidsanslutningen), och
//  - om idleAfter är satt: när den stått synlig men orörd så länge (elevvyer;
//    dashboard och topplista kan stå på en projektor och somnar aldrig då).
// Den vaknar när den syns igen respektive vid första tryck/tangent, och
// skärmen hämtar då senaste läget direkt. Allt räknas per flik — hur gammal
// tRAT:en är eller om den varit stängd spelar ingen roll, så en tRAT som
// testats på måndag och körs på fredag påverkas inte.

const HIDDEN_SLEEP = 30 * 60 * 1000;
const STUDENT_IDLE_SLEEP = 4 * 60 * 60 * 1000;
const IDLE_CHECK = 60 * 1000;
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"];

function startTabSleep(onChange, { hiddenAfter = HIDDEN_SLEEP, idleAfter = null, now = () => Date.now() } = {}) {
  if (typeof document === "undefined") return () => {};
  let asleep = false;
  let stopped = false;
  let lastActivity = now();
  let hiddenTimer = null;
  let idleTimer = null;
  const hidden = () => document.visibilityState === "hidden";

  const set = v => { if (asleep !== v && !stopped) { asleep = v; onChange(v); } };

  function onVisibility() {
    clearTimeout(hiddenTimer);
    if (hidden()) {
      hiddenTimer = setTimeout(() => set(true), hiddenAfter);
    } else {
      lastActivity = now();
      set(false);
    }
  }

  function onActivity() {
    lastActivity = now();
    if (!hidden()) set(false);
  }

  function checkIdle() {
    if (stopped) return;
    if (!hidden() && now() - lastActivity >= idleAfter) set(true);
    idleTimer = setTimeout(checkIdle, IDLE_CHECK);
  }

  document.addEventListener("visibilitychange", onVisibility);
  if (idleAfter) {
    ACTIVITY_EVENTS.forEach(e => window.addEventListener(e, onActivity, { capture: true, passive: true }));
    idleTimer = setTimeout(checkIdle, IDLE_CHECK);
  }
  if (hidden()) onVisibility();

  return () => {
    stopped = true;
    clearTimeout(hiddenTimer);
    clearTimeout(idleTimer);
    document.removeEventListener("visibilitychange", onVisibility);
    if (idleAfter) ACTIVITY_EVENTS.forEach(e => window.removeEventListener(e, onActivity, { capture: true }));
  };
}

// asleep: fliken sover nu. hasSlept: den har sovit minst en gång, så att en
// hämtning som startas när den vaknar kan köras direkt i stället för att vänta.
function useTabSleep(opts = {}) {
  const [asleep, setAsleep] = useState(false);
  const sleptRef = useRef(false);
  if (asleep) sleptRef.current = true;
  const { hiddenAfter, idleAfter } = opts;
  useEffect(() => startTabSleep(setAsleep, { hiddenAfter, idleAfter }), [hiddenAfter, idleAfter]);
  return { asleep, hasSlept: sleptRef.current };
}

export { startTabSleep, useTabSleep, HIDDEN_SLEEP, STUDENT_IDLE_SLEEP };
